import React, { useCallback, useEffect, useRef, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import Modal from '../../components/modal';
import Skeleton from '../../components/Skeleton';
import ReactPaginate from 'react-paginate';
import { useInternals } from '../../context/internalContext';
import { useUsers } from '../../context/usersContext';
import { toast } from 'react-toastify';
import useSubmitLock from '../../utils/useSubmitLock';
import PlusCircle from '../../assets/icons/pluscircle.svg';
import './sacolao.css';

const getCurrentDateValue = () => new Date().toISOString().slice(0, 10);

const mapPackageDeliveryToRecord = (item) => ({
  id: item.id,
  date: item.date,
  visitorName: item.visitor_name || '',
  internalName: item.internal_name || '',
  observation: item.observations || '',
  isApproved: item.is_approved,
});

function Sacolao() {
  const { getVisitors, listInternals, createPackageDelivery, listPackageDeliveries } = useInternals();
  const { buildUnitReadParams, requireWriteUnit } = useUsers();
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const [sacolaoList, setSacolaoList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(false);

  const [filterVisitorName, setFilterVisitorName] = useState('');
  const [filterInternalName, setFilterInternalName] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  // Debounce dos filtros de nome (visitante/interno): guarda a busca que ainda espera o usuário parar de digitar.
  const nameFilterTimerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [entryDate, setEntryDate] = useState(getCurrentDateValue());

  const [visitorName, setVisitorName] = useState('');
  const [visitorSuggestions, setVisitorSuggestions] = useState([]);
  const [visitorId, setVisitorId] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState(null);

  const [linkedInternals, setLinkedInternals] = useState([]);
  const [isLoadingLinkedInternals, setIsLoadingLinkedInternals] = useState(false);
  const [selectedInternalId, setSelectedInternalId] = useState('');

  const [isApproved, setIsApproved] = useState('');
  const [observation, setObservation] = useState('');

  const normalizeText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

  const getVisitorAccessStatus = (visitor) => {
    if (!visitor) {
      return { label: 'NAO LIBERADO', className: 'status-nao-liberado' };
    }

    if (visitor.authorize_entry === true) {
      return { label: 'LIBERADO', className: 'status-liberado' };
    }

    const discipline = normalizeText(visitor.discipline_authorization);
    const socialWorker = normalizeText(visitor.social_worker_authorization);
    const hasTechnicalAuthorization = discipline === 'AUTORIZADO' && socialWorker === 'AUTORIZADO';

    if (visitor.authorize_entry === false) {
      return { label: 'BLOQUEADO', className: 'status-bloqueado' };
    }

    if (hasTechnicalAuthorization) {
      return { label: 'PENDENTE DE LIBERACAO', className: 'status-nao-liberado' };
    }

    return { label: 'NAO LIBERADO', className: 'status-nao-liberado' };
  };

  const getInternalVisitStatus = (internal) => {
    if (!internal || internal.visit === undefined || internal.visit === null) {
      return null;
    }

    if (internal.visit === false) {
      return { label: 'VISITA BLOQUEADA', className: 'status-bloqueado' };
    }

    return { label: 'VISITA LIBERADA', className: 'status-liberado' };
  };

  const getApprovalStatus = (isApprovedValue) => {
    if (isApprovedValue === true) {
      return { label: 'APROVADA', className: 'status-liberado' };
    }

    if (isApprovedValue === false) {
      return { label: 'NAO APROVADA', className: 'status-bloqueado' };
    }

    return { label: 'NAO INFORMADO', className: 'status-nao-liberado' };
  };

  // Filtros de nome e data ainda não são suportados pela API; assim que o backend
  // adicionar visitor_name/internal_name/start_date/end_date, a busca passa a filtrar de verdade.
  const fetchSacolaoList = useCallback(async ({
    visitorName = '',
    internalName = '',
    startDate = '',
    endDate = '',
    page = 0,
    limit = 10,
  } = {}) => {
    // Busca imediata (data, página, limpar) cancela a de nome pendente, que viria com filtros antigos.
    clearTimeout(nameFilterTimerRef.current);
    setIsLoadingList(true);
    try {
      const extra = {};
      if (visitorName) extra.visitor_name = visitorName;
      if (internalName) extra.internal_name = internalName;
      if (startDate) extra.start_date = startDate;
      if (endDate) extra.end_date = endDate;

      const response = await listPackageDeliveries(buildUnitReadParams({ limit, offset: page * limit, ...extra }));

      if (response && Array.isArray(response.results)) {
        setSacolaoList(response.results.map(mapPackageDeliveryToRecord));
        setTotalCount(response.count || 0);
        setCurrentPage(page);
      } else {
        setSacolaoList([]);
        setTotalCount(0);
        setCurrentPage(0);
      }
    } finally {
      setIsLoadingList(false);
    }
  }, [listPackageDeliveries, buildUnitReadParams]);

  useEffect(() => {
    fetchSacolaoList();
  }, [fetchSacolaoList]);

  useEffect(() => (
    () => clearTimeout(nameFilterTimerRef.current)
  ), []);

  const hasLetters = (value) => /[a-zA-ZÀ-ÿ]/.test(value || '');
  const isCpfSearchMode = (value) => !hasLetters(value) && (value || '').replace(/\D/g, '').length > 0;

  const handleVisitorSearch = async (query) => {
    setVisitorName(query);
    setVisitorId('');
    setSelectedVisitor(null);
    setLinkedInternals([]);
    setSelectedInternalId('');

    const cpfDigits = (query || '').replace(/\D/g, '');

    if (isCpfSearchMode(query)) {
      if (cpfDigits.length >= 11) {
        const response = await getVisitors({ cpf: cpfDigits });
        setVisitorSuggestions(Array.isArray(response?.results) ? response.results : []);
      } else {
        setVisitorSuggestions([]);
      }
      return;
    }

    if (query.length >= 3) {
      const response = await getVisitors({ name: query });
      setVisitorSuggestions(Array.isArray(response?.results) ? response.results : []);
    } else {
      setVisitorSuggestions([]);
    }
  };

  const hydrateLinkedInternal = useCallback(async (internalData) => {
    const internalId = internalData?.id;
    if (!internalId) return null;

    try {
      const response = await listInternals(buildUnitReadParams({ id: internalId }));
      // Sem resultado aqui significa que o interno não pertence à unidade ativa do usuário
      // (buildUnitReadParams filtra por unidade) — nesse caso ele não deve aparecer na lista.
      return response?.results?.[0] || null;
    } catch (error) {
      // Erro de rede/instabilidade: mantém dados mínimos para não travar a tela.
      return {
        id: internalId,
        name: internalData?.name || '',
        cpf: internalData?.cpf || '',
        pavilion_name: internalData?.pavilion_name || '',
        cell_name: internalData?.cell_name || '',
      };
    }
  }, [listInternals, buildUnitReadParams]);

  const handleSelectVisitor = async (visitor) => {
    setVisitorId(visitor.id);
    setSelectedVisitor(visitor);
    setVisitorName(visitor.name);
    setVisitorSuggestions([]);
    setSelectedInternalId('');

    const visitorInternals = Array.isArray(visitor?.internals) ? visitor.internals : [];
    if (!visitorInternals.length) {
      setLinkedInternals([]);
      return;
    }

    setIsLoadingLinkedInternals(true);
    try {
      const hydrated = await Promise.all(
        visitorInternals.map((internalData) => hydrateLinkedInternal(internalData))
      );

      const uniqueById = [];
      hydrated.filter(Boolean).forEach((internalData) => {
        if (!uniqueById.some((item) => String(item.id) === String(internalData.id))) {
          uniqueById.push(internalData);
        }
      });
      setLinkedInternals(uniqueById);
    } finally {
      setIsLoadingLinkedInternals(false);
    }
  };

  const resetForm = () => {
    setVisitorName('');
    setVisitorId('');
    setSelectedVisitor(null);
    setVisitorSuggestions([]);
    setLinkedInternals([]);
    setSelectedInternalId('');
    setIsApproved('');
    setObservation('');
    setEntryDate(getCurrentDateValue());
  };

  const openModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    await runWithSubmitLock(async () => {
      if (!visitorId) {
        toast.error('Selecione um visitante.');
        return;
      }

      if (!selectedInternalId) {
        toast.error('Selecione o interno que está recebendo o sacolão.');
        return;
      }

      if (!entryDate) {
        toast.error('Informe a data do registro.');
        return;
      }

      if (isApproved === '') {
        toast.error('Informe se a revista foi aprovada.');
        return;
      }

      if (isApproved === 'false' && !observation.trim()) {
        toast.error('Observação obrigatória quando a revista não é aprovada.');
        return;
      }

      const writeUnit = requireWriteUnit();
      if (!writeUnit.ok) {
        toast.warning(writeUnit.message);
        return;
      }

      const now = new Date();
      const [entryYear, entryMonth, entryDay] = entryDate.split('-').map(Number);
      const entryDateTime = new Date(
        entryYear, entryMonth - 1, entryDay, now.getHours(), now.getMinutes(), now.getSeconds()
      );

      const payload = {
        date: Number.isNaN(entryDateTime.getTime()) ? new Date().toISOString() : entryDateTime.toISOString(),
        visitor: visitorId,
        internal: [selectedInternalId],
        unit: writeUnit.unitId,
        observations: observation.trim(),
        is_approved: isApproved === 'true',
      };

      const created = await createPackageDelivery(payload);
      if (!created) return;

      closeModal();
      fetchSacolaoList({
        visitorName: filterVisitorName,
        internalName: filterInternalName,
        startDate: filterStartDate,
        endDate: filterEndDate,
        page: 0,
        limit: itemsPerPage,
      });
    });
  };

  const visitorCpfDigits = (visitorName || '').replace(/\D/g, '');
  const canShowVisitorResults = isCpfSearchMode(visitorName)
    ? visitorCpfDigits.length >= 11
    : visitorName.length >= 3;
  const selectedVisitorStatus = getVisitorAccessStatus(selectedVisitor);
  const selectedInternalData = linkedInternals.find(
    (internal) => String(internal.id) === String(selectedInternalId)
  ) || null;
  const selectedInternalStatus = getInternalVisitStatus(selectedInternalData);

  // Filtros de nome com debounce: só consulta a API quando o usuário para de digitar.
  const handleFilterVisitorNameChange = (value) => {
    setFilterVisitorName(value);
    clearTimeout(nameFilterTimerRef.current);
    if (value.length === 0 || value.length >= 3) {
      nameFilterTimerRef.current = setTimeout(() => {
        fetchSacolaoList({
          visitorName: value,
          internalName: filterInternalName,
          startDate: filterStartDate,
          endDate: filterEndDate,
          page: 0,
          limit: itemsPerPage,
        });
      }, 400);
    }
  };

  const handleFilterInternalNameChange = (value) => {
    setFilterInternalName(value);
    clearTimeout(nameFilterTimerRef.current);
    if (value.length === 0 || value.length >= 3) {
      nameFilterTimerRef.current = setTimeout(() => {
        fetchSacolaoList({
          visitorName: filterVisitorName,
          internalName: value,
          startDate: filterStartDate,
          endDate: filterEndDate,
          page: 0,
          limit: itemsPerPage,
        });
      }, 400);
    }
  };

  const handleFilterStartDateChange = (value) => {
    setFilterStartDate(value);
    fetchSacolaoList({
      visitorName: filterVisitorName,
      internalName: filterInternalName,
      startDate: value,
      endDate: filterEndDate,
      page: 0,
      limit: itemsPerPage,
    });
  };

  const handleFilterEndDateChange = (value) => {
    setFilterEndDate(value);
    fetchSacolaoList({
      visitorName: filterVisitorName,
      internalName: filterInternalName,
      startDate: filterStartDate,
      endDate: value,
      page: 0,
      limit: itemsPerPage,
    });
  };

  const resetFilters = () => {
    setFilterVisitorName('');
    setFilterInternalName('');
    setFilterStartDate('');
    setFilterEndDate('');
    fetchSacolaoList({ page: 0, limit: itemsPerPage });
  };

  const pageCount = Math.ceil(totalCount / itemsPerPage);

  const formatDateBR = (value) => {
    if (!value) return 'N/A';
    try {
      return new Date(value).toLocaleDateString('pt-BR');
    } catch {
      return value;
    }
  };

  const formatTimeBR = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className='container'>
        <Titulo
          children={<h2>Sacolão</h2>}
          children2={
            <button className='btnAdcionar' onClick={openModal}>
              <img src={PlusCircle} alt='icone' /> Adicionar
            </button>
          }
        />

        <div className='sacolao-filters-bar'>
          <div className='sacolao-filter-item'>
            <label>Visitante</label>
            <FormattedInput
              type='nome'
              placeholder='Filtrar por visitante (mín. 3 letras)'
              value={filterVisitorName}
              onChange={(e) => handleFilterVisitorNameChange(e.target.value)}
            />
          </div>

          <div className='sacolao-filter-item'>
            <label>Interno</label>
            <FormattedInput
              type='nome'
              placeholder='Filtrar por interno (mín. 3 letras)'
              value={filterInternalName}
              onChange={(e) => handleFilterInternalNameChange(e.target.value)}
            />
          </div>

          <div className='sacolao-filter-item'>
            <label>Data início</label>
            <input
              type='date'
              value={filterStartDate}
              onChange={(e) => handleFilterStartDateChange(e.target.value)}
            />
          </div>

          <div className='sacolao-filter-item'>
            <label>Data término</label>
            <input
              type='date'
              value={filterEndDate}
              onChange={(e) => handleFilterEndDateChange(e.target.value)}
            />
          </div>

          <div className='sacolao-filter-actions'>
            <button type='button' className='btn-reset btnAdcionar' onClick={resetFilters}>
              Limpar filtros
            </button>
          </div>
        </div>

        <div className='content content-table'>
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Hora</th>
                <th>Visitante</th>
                <th>Interno</th>
                <th>Revista Aprovada</th>
                <th>Ocorrência/Observação</th>
              </tr>
            </thead>
            {isLoadingList ? (
              <Skeleton type='table' rows={5} cols={6} prefix='sacolao' />
            ) : sacolaoList.length > 0 ? (
              <tbody>
                {sacolaoList.map((item) => {
                  const approvalStatus = getApprovalStatus(item.isApproved);
                  return (
                    <tr key={item.id}>
                      <td>{formatDateBR(item.date)}</td>
                      <td>{formatTimeBR(item.date)}</td>
                      <td className='td-limited'>{item.visitorName}</td>
                      <td className='td-limited'>{item.internalName}</td>
                      <td>
                        <span className={`sacolao-visitor-status ${approvalStatus.className}`}>
                          {approvalStatus.label}
                        </span>
                      </td>
                      <td className='sacolao-observation-cell'>{item.observation || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td className='semDados' colSpan='6'>Nenhum registro de sacolão encontrado.</td>
                </tr>
              </tbody>
            )}
          </table>

          {totalCount > 0 && (
            <div className='pagination-options'>
              <div>
                <label htmlFor='sacolao-per-page'>Itens por página:</label>
                <select
                  id='sacolao-per-page'
                  value={itemsPerPage}
                  onChange={(e) => {
                    const newLimit = Number(e.target.value);
                    setItemsPerPage(newLimit);
                    fetchSacolaoList({
                      visitorName: filterVisitorName,
                      internalName: filterInternalName,
                      startDate: filterStartDate,
                      endDate: filterEndDate,
                      page: 0,
                      limit: newLimit,
                    });
                  }}
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {pageCount > 1 && (
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  breakLabel={'...'}
                  pageCount={pageCount}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  onPageChange={({ selected }) => fetchSacolaoList({
                    visitorName: filterVisitorName,
                    internalName: filterInternalName,
                    startDate: filterStartDate,
                    endDate: filterEndDate,
                    page: selected,
                    limit: itemsPerPage,
                  })}
                  containerClassName={'pagination'}
                  activeClassName={'activeNumber'}
                  previousClassName={'prev'}
                  nextClassName={'next'}
                  pageClassName={'page'}
                  breakClassName={'break-me'}
                  forcePage={currentPage}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isModalOpen} closeModal={closeModal}>
        <h4>Registrar Sacolão</h4>
        <form onSubmit={handleSubmit}>
          <div className='form-group-large'>
            <label htmlFor='sacolao-entry-date' className='sacolao-field-label'>
              Data
            </label>
            <input
              id='sacolao-entry-date'
              type='date'
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              required
            />
          </div>

          <div className='form-group-large sacolao-search-wrap'>
            <label htmlFor='sacolao-visitor-search' className='sacolao-field-label'>
              Visitante
            </label>
            {isCpfSearchMode(visitorName) ? (
              <FormattedInput
                id='sacolao-visitor-search'
                type='cpf'
                placeholder='Buscar visitante por CPF'
                value={visitorName}
                onChange={(e) => handleVisitorSearch(e.target.value)}
              />
            ) : (
              <FormattedInput
                id='sacolao-visitor-search'
                type='nome'
                placeholder='Buscar visitante por nome ou CPF'
                value={visitorName}
                onChange={(e) => handleVisitorSearch(e.target.value)}
              />
            )}
            <small className='sacolao-field-help'>
              Você pode buscar por nome ou CPF.
            </small>

            {selectedVisitor && (
              <small className='sacolao-field-help'>
                Status do visitante:{' '}
                <span className={`sacolao-visitor-status ${selectedVisitorStatus.className}`}>
                  {selectedVisitorStatus.label}
                </span>
              </small>
            )}

            {canShowVisitorResults && visitorSuggestions.length > 0 && (
              <div className='sacolao-search-results'>
                {visitorSuggestions.map((visitor) => {
                  const status = getVisitorAccessStatus(visitor);
                  return (
                    <div
                      key={visitor.id}
                      onClick={() => handleSelectVisitor(visitor)}
                      className='sacolao-search-result-item'
                    >
                      {visitor.name} - {visitor.cpf} -{' '}
                      <span className={`sacolao-visitor-status ${status.className}`}>
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className='form-group-large'>
            <label htmlFor='sacolao-internal-select' className='sacolao-field-label'>
              Interno relacionado
            </label>
            <select
              id='sacolao-internal-select'
              value={selectedInternalId}
              onChange={(e) => setSelectedInternalId(e.target.value)}
              disabled={!selectedVisitor || isLoadingLinkedInternals || linkedInternals.length === 0}
            >
              <option value=''>
                {!selectedVisitor
                  ? 'Selecione primeiro um visitante'
                  : isLoadingLinkedInternals
                    ? 'Carregando internos vinculados...'
                    : linkedInternals.length === 0
                      ? 'Nenhum interno vinculado para essa unidade'
                      : 'Selecione o interno que está recebendo o sacolão'}
              </option>
              {linkedInternals.map((internal) => {
                const internalStatus = getInternalVisitStatus(internal);
                return (
                  <option key={internal.id} value={internal.id}>
                    {internal.name}{internalStatus ? ` — ${internalStatus.label}` : ''}
                  </option>
                );
              })}
            </select>
            <small className='sacolao-field-help'>
              Internos vinculados ao visitante selecionado.
            </small>
            {selectedInternalStatus && (
              <small className='sacolao-field-help'>
                Status do interno:{' '}
                <span className={`sacolao-visitor-status ${selectedInternalStatus.className}`}>
                  {selectedInternalStatus.label}
                </span>
              </small>
            )}
          </div>

          <div className='form-group-large'>
            <label htmlFor='sacolao-is-approved' className='sacolao-field-label'>
              Revista aprovada
            </label>
            <select
              id='sacolao-is-approved'
              value={isApproved}
              onChange={(e) => setIsApproved(e.target.value)}
              required
            >
              <option value=''>Selecione</option>
              <option value='true'>Sim</option>
              <option value='false'>Não</option>
            </select>
          </div>

          <div className='form-group-large'>
            <label htmlFor='sacolao-observation' className='sacolao-field-label'>
              Ocorrência / Observação
            </label>
            <textarea
              id='sacolao-observation'
              placeholder='Descreva aqui caso tenha encontrado algo no sacolão ou alguma observação'
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              required={isApproved === 'false'}
            />
            {isApproved === 'false' && (
              <small className='sacolao-field-help'>
                Obrigatório quando a revista não é aprovada.
              </small>
            )}
          </div>

          <button type='submit' className='btnAcao' disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </button>
        </form>
      </Modal>
    </div>
  );
}

export default Sacolao;
